import last from "lodash/last";

import { Day } from "../../../../booking_module_calendar/booking_calendar/booking_calendar_store";
import { addDays, getDaysDifference, setTimeToMidnight } from "../../../calendar.utils";
import { DnDMode } from "./index";

type GetLimitedDays = {
  daysDiff: number;
  maxDaysToLeft: number;
  maxDaysToRight: number;
};

const getLimitedDays = ({ daysDiff, maxDaysToLeft, maxDaysToRight }: GetLimitedDays): number => {
  // daysDiff below 0 means expanding bar to the previous dates
  if (daysDiff < maxDaysToLeft) {
    return maxDaysToLeft;
  } else if (daysDiff > maxDaysToRight) {
    return maxDaysToRight;
  }
  return daysDiff;
};

type GetDatesData = {
  days: Day[];
  minDate: Date;
  bookingStartDate: Date;
  bookingEndDate: Date;
  daysDiff: number;
};
type GetDatesDataReturn = {
  startDate: Date;
  endDate: Date;
  daysDiffLimited: number;
};

const getMinDate = (days: Day[], bookingPastDaysLimit: number) => {
  const today = days.find(({ isToday }) => isToday)?.date ?? setTimeToMidnight(new Date());
  return setTimeToMidnight(addDays(new Date(today.getTime()), -1 * bookingPastDaysLimit));
};

const getDatesForStartEnd = ({
  days,
  minDate,
  bookingStartDate,
  bookingEndDate,
  daysDiff
}: GetDatesData): GetDatesDataReturn => {
  const daysDiffFromBookingStartToMinDate = getDaysDifference(
    setTimeToMidnight(new Date(bookingStartDate.getTime())),
    minDate
  );
  const daysDiffFromBookingEndToCalendarEnd = getDaysDifference(
    setTimeToMidnight(new Date(bookingEndDate.getTime())),
    last(days)!.date
  );

  const maxDaysToLeft = Math.min(daysDiffFromBookingStartToMinDate, 0);
  const maxDaysToRight = Math.max(daysDiffFromBookingEndToCalendarEnd, 0);
  const daysDiffLimited = getLimitedDays({ daysDiff, maxDaysToLeft, maxDaysToRight });

  const startDate = addDays(bookingStartDate, daysDiffLimited);
  const endDate = addDays(bookingEndDate, daysDiffLimited);
  return {
    startDate,
    endDate,
    daysDiffLimited
  };
};

const getDatesForStart = ({
  minDate,
  bookingStartDate,
  bookingEndDate,
  daysDiff
}: Omit<GetDatesData, "days">): GetDatesDataReturn => {
  const daysDiffFromBookingStartToMinDate = getDaysDifference(
    setTimeToMidnight(new Date(bookingStartDate.getTime())),
    minDate
  );
  const daysDiffFromBookingStartToBookingEnd = getDaysDifference(
    setTimeToMidnight(new Date(bookingStartDate.getTime())),
    setTimeToMidnight(new Date(bookingEndDate.getTime()))
  );

  const maxDaysToLeft = Math.min(daysDiffFromBookingStartToMinDate, 0);
  const maxDaysToRight = Math.max(daysDiffFromBookingStartToBookingEnd, 0);
  const daysDiffLimited = getLimitedDays({ daysDiff, maxDaysToLeft, maxDaysToRight });

  const startDate = addDays(bookingStartDate, daysDiffLimited);
  return {
    startDate,
    endDate: bookingEndDate,
    daysDiffLimited
  };
};

const getDatesForEnd = ({
  days,
  bookingStartDate,
  bookingEndDate,
  daysDiff
}: Omit<GetDatesData, "minDate">): GetDatesDataReturn => {
  const daysDiffFromBookingEndToBookingStart = getDaysDifference(
    setTimeToMidnight(new Date(bookingEndDate.getTime())),
    setTimeToMidnight(new Date(bookingStartDate.getTime()))
  );
  const daysDiffFromBookingEndToCalendarEnd = getDaysDifference(
    setTimeToMidnight(new Date(bookingEndDate.getTime())),
    last(days)!.date
  );

  const maxDaysToLeft = Math.min(daysDiffFromBookingEndToBookingStart, 0);
  const maxDaysToRight = Math.max(daysDiffFromBookingEndToCalendarEnd, 0);
  const daysDiffLimited = getLimitedDays({ daysDiff, maxDaysToLeft, maxDaysToRight });

  const endDate = addDays(bookingEndDate, daysDiffLimited);
  return {
    startDate: bookingStartDate,
    endDate,
    daysDiffLimited
  };
};

type GetUpdatedDatesReturn = {
  startDate: Date;
  endDate: Date;
  daysDiffLimited: number;
};

const getUpdatedDates = (
  mode: DnDMode,
  daysDiff: number,
  bookingStartDateString: string,
  bookingEndDateString: string,
  days: Day[],
  bookingPastDaysLimit: number
): GetUpdatedDatesReturn => {
  const minDate = getMinDate(days, bookingPastDaysLimit);
  const bookingStartDate = new Date(bookingStartDateString);
  const bookingEndDate = new Date(bookingEndDateString);

  switch (mode) {
    case DnDMode.Horizontal:
    case DnDMode.HorizontalVertical:
      return getDatesForStartEnd({
        days,
        minDate,
        bookingStartDate,
        bookingEndDate,
        daysDiff
      });
    case DnDMode.StartEdge:
      return getDatesForStart({
        minDate,
        bookingStartDate,
        bookingEndDate,
        daysDiff
      });
    case DnDMode.EndEdge:
      return getDatesForEnd({
        days,
        bookingStartDate,
        bookingEndDate,
        daysDiff
      });
  }
};

export default getUpdatedDates;
